import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "inputContainer", "fileInput", "fileSelectedDisplay", "dropText", "pasteButton", "pasteStatus", "archiveToggle", "markAsWatchedContainer", "markAsWatchedToggle"]
  static values = {
    pasteButtonTitle: String,
    pasteButtonUnavailableTitle: String,
    pasteButtonUnavailableStatus: String
  }

  connect() {
    this.autoPasteAttempted = false
    this.clipboardAvailable = this.clipboardReadSupported()

    this.updatePasteButtonState()
    this.autoResize()
    this.syncMarkAsWatchedVisibility()

    // In a dialog, the form can connect before it's visible; recalculate once laid out.
    requestAnimationFrame(() => this.autoResize())
    setTimeout(() => this.autoResize(), 50)
  }

  async autoPasteOnFocus() {
    if (this.autoPasteAttempted) {
      return
    }

    this.autoPasteAttempted = true
    await this.tryPasteFromClipboard()
  }

  async pasteFromClipboard(event) {
    event.preventDefault()

    if (!this.clipboardAvailable) {
      return
    }

    await this.tryPasteFromClipboard()
  }

  autoResize() {
    const input = this.inputTarget
    input.style.height = "auto"
    input.style.height = Math.max(input.scrollHeight, 96) + "px"
  }

  syncMarkAsWatchedVisibility() {
    if (!this.hasMarkAsWatchedContainerTarget) {
      return
    }

    const shouldShow = this.youtubeInputActive() && this.archiveEnabled()

    this.markAsWatchedContainerTarget.hidden = !shouldShow

    if (!shouldShow && this.hasMarkAsWatchedToggleTarget) {
      this.markAsWatchedToggleTarget.checked = false
    }
  }

  async tryPasteFromClipboard() {
    if (!this.clipboardReadSupported() || !this.inputIsEmpty()) {
      return
    }

    let clipboardText

    try {
      clipboardText = (await navigator.clipboard.readText()).trim()
    } catch {
      return
    }

    if (!this.urlLike(clipboardText)) {
      return
    }

    this.inputTarget.value = clipboardText
    this.autoResize()
    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }))
  }

  clipboardReadSupported() {
    return typeof navigator !== "undefined" &&
      navigator.clipboard &&
      typeof navigator.clipboard.readText === "function"
  }

  inputIsEmpty() {
    return this.inputTarget.value.trim().length === 0
  }

  urlLike(value) {
    if (!value) {
      return false
    }

    const candidate = this.normalizedUrlCandidate(value)

    if (!candidate) {
      return false
    }

    try {
      const parsed = new URL(candidate)
      return ["http:", "https:"].includes(parsed.protocol) && parsed.hostname.includes(".")
    } catch {
      return false
    }
  }

  normalizedUrlCandidate(value) {
    const trimmedValue = value.trim()

    if (trimmedValue.length === 0 || /\s/.test(trimmedValue)) {
      return null
    }

    if (/^[a-zA-Z][a-zA-Z\d+\-.]*:\/\//.test(trimmedValue)) {
      return trimmedValue
    }

    return `https://${trimmedValue}`
  }

  youtubeInputActive() {
    if (this.fileInputTarget.files.length > 0) {
      return false
    }

    return this.youtubeVideoIdCandidate(this.inputTarget.value) !== null
  }

  youtubeVideoIdCandidate(value) {
    const candidate = this.normalizedUrlCandidate(value)

    if (candidate === null) {
      return null
    }

    let parsed

    try {
      parsed = new URL(candidate)
    } catch {
      return null
    }

    const host = parsed.hostname.toLowerCase()
    const segments = parsed.pathname.split("/").filter(Boolean)

    if (host === "youtu.be") {
      return this.normalizedYouTubeVideoId(segments[0])
    }

    if (!(host === "youtube.com" || host.endsWith(".youtube.com"))) {
      return null
    }

    if (segments[0] === "watch") {
      return this.normalizedYouTubeVideoId(parsed.searchParams.get("v"))
    }

    if (["embed", "shorts", "live"].includes(segments[0])) {
      return this.normalizedYouTubeVideoId(segments[1])
    }

    return null
  }

  normalizedYouTubeVideoId(candidate) {
    const videoId = candidate?.trim()

    return videoId && /^[\w-]{11}$/.test(videoId) ? videoId : null
  }

  archiveEnabled() {
    return this.hasArchiveToggleTarget && this.archiveToggleTarget.checked
  }

  updatePasteButtonState() {
    if (!this.hasPasteButtonTarget) {
      return
    }

    this.pasteButtonTarget.disabled = !this.clipboardAvailable
    this.pasteButtonTarget.ariaDisabled = (!this.clipboardAvailable).toString()

    this.pasteButtonTarget.classList.toggle("opacity-50", !this.clipboardAvailable)
    this.pasteButtonTarget.classList.toggle("cursor-not-allowed", !this.clipboardAvailable)

    this.pasteButtonTarget.title = this.clipboardAvailable ? this.pasteButtonTitleValue : this.pasteButtonUnavailableTitleValue

    if (!this.hasPasteStatusTarget) {
      return
    }

    this.pasteStatusTarget.textContent = this.clipboardAvailable ? "" : this.pasteButtonUnavailableStatusValue
  }

  dragOver(event) {
    event.preventDefault()
    this.inputTarget.dataset.dragOver = "true"
    this.fileSelectedDisplayTarget.dataset.dragOver = "true"
  }

  dragLeave(event) {
    if (!this.inputContainerTarget.contains(event.relatedTarget)) {
      delete this.inputTarget.dataset.dragOver
      delete this.fileSelectedDisplayTarget.dataset.dragOver
    }
  }

  drop(event) {
    event.preventDefault()
    delete this.inputTarget.dataset.dragOver
    delete this.fileSelectedDisplayTarget.dataset.dragOver

    const files = event.dataTransfer.files
    if (files.length > 0) {
      this.fileInputTarget.files = files
      this.fileSelected()
    }
  }

  fileSelected() {
    const file = this.fileInputTarget.files[0]
    if (!file) return

    this.inputTarget.value = ""
    this.inputTarget.style.display = "none"
    if (this.hasPasteButtonTarget) this.pasteButtonTarget.style.display = "none"
    this.dropTextTarget.textContent = file.name
    this.fileSelectedDisplayTarget.style.display = "flex"
    this.syncMarkAsWatchedVisibility()
  }

  clearFile() {
    this.fileInputTarget.value = ""
    this.fileSelectedDisplayTarget.style.display = ""
    this.dropTextTarget.textContent = ""
    this.inputTarget.style.display = ""
    if (this.hasPasteButtonTarget) this.pasteButtonTarget.style.display = ""
    this.autoResize()
    this.syncMarkAsWatchedVisibility()
  }
}
